/**
 * Detalle de una plantilla (`plantillas/:id`): en qué estado está en Meta, qué hacer según ese
 * estado, cómo se ve, dónde se usa y su historia.
 *
 * Las acciones dependen del estado y del contrato del backend (SPEC-backend §9.3): una plantilla
 * que ya llegó a Meta no se edita, se DUPLICA (versión nueva, nombre técnico nuevo); un
 * «Borrador en Twilio» (`unsubmitted`) se reintenta sin crear otro Content; borrar algo que llegó a
 * Meta reserva el nombre por 30 días.
 *
 * Con la plantilla en revisión, el detalle se refresca cada 30 s: la aprobación llega sola.
 *
 * Las métricas salen de `/estadisticas/plantillas/:id` (los últimos 400 días, el máximo que acepta el
 * backend): enviados, las cuatro tasas y el gasto, cuánto tardó Meta en aprobarla y los botones
 * tocados. Si ese pedido falla, se siguen mostrando los tres números de siempre (`p.metricas`): una
 * estadística que no llega no esconde lo que ya se sabía.
 */

import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  AlertTriangle, Archive, ArrowLeft, CheckCheck, CheckCircle2, ChevronDown, ChevronRight, Copy, Eye, Loader2, Mail, MessageSquare, MousePointerClick, Pencil,
  RefreshCw, Replace, Send, Trash2, UserX, Wallet, X,
} from "lucide-react";
import type { CampoDisponible, PlantillaDetalle, HistorialPlantilla } from "../types";
import { oficialApi } from "../api";
import { estadisticasApi } from "../estadisticasApi";
import type { EstadisticasPlantilla } from "../types.estadisticas";
import { BASE_TASA, MAX_DIAS_RANGO, diaEnZona, sobreLaBase, sumarDias, tasaTexto } from "../logic/estadisticas";
import { esErrorDeClave, leerError, leerRespuesta, TEXTO_ERROR_CLAVE, textoError } from "../logic/respuesta";
import { intervaloPlantillas } from "../logic/polling";
import { conEjemplos, desdeDetalle, etiquetaDelCampo, previaLocal } from "../logic/plantilla";
import { CATEGORIA, CHIP_PLANTILLA, ESTADO_PLANTILLA, TIPO_PLANTILLA, accionRechazo, ayudaEstadoPlantilla, motivoPlantillaInutilizable, motivoPlantillaNoUsable } from "../logic/textos";
import { useCuentaOficial } from "../contexto";
import { horasTexto, nombreDeQuienPara, usd, usdPorMensaje } from "../logic/formato";
import { usePolling } from "../hooks/usePolling";
import { ChipEstadoPlantilla } from "../components/ChipEstadoPlantilla";
import { ChipEstadoCampanaOficial } from "../components/ChipEstadoCampanaOficial";
import { PreviaWhatsApp } from "../components/PreviaWhatsApp";
import { Tarjeta } from "../components/Tarjeta";
import StatTile from "../../campana-nueva/components/StatTile";
import ConfirmDangerModal from "../../../shared/components/ui/ConfirmDangerModal";
import { fecha, fechaHora, num } from "../../campana-nueva/utils/format";
import { SoloEquipo } from "../../campana-nueva/components/SoloEquipo";
import { useEsEquipoDeenex } from "../../campana-nueva/hooks/useEsEquipoDeenex";

const R = "/activa/oficial";
const enc = encodeURIComponent;

const FUENTE_HISTORIAL: Record<HistorialPlantilla["fuente"], string> = {
  api: "al mandarla",
  evento: "aviso de Meta",
  polling: "consulta periódica",
  simulado: "simulación",
  envio: "al enviar",
};

type Mensaje = { tono: "ok" | "error" | "aviso"; texto: string; duplicar?: boolean };

export default function PlantillaDetallePage() {
  const { id = "" } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { modo } = useCuentaOficial();

  const [p, setP] = useState<PlantillaDetalle | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");
  const [noExiste, setNoExiste] = useState(false);
  const equipo = useEsEquipoDeenex();
  const [mensaje, setMensaje] = useState<Mensaje | null>(() => {
    const a = (location.state as { aviso?: string } | null)?.aviso;
    return a ? { tono: "ok", texto: a } : null;
  });
  const [ocupado, setOcupado] = useState<string>("");
  const [confirmarBorrar, setConfirmarBorrar] = useState(false);
  const [reemplazadas, setReemplazadas] = useState<{ id: string; nombre: string; volvioABorrador: boolean }[] | null>(null);
  const [verTecnicos, setVerTecnicos] = useState(false);
  const [stats, setStats] = useState<EstadisticasPlantilla | null>(null);
  /** Desde qué día cuentan las métricas (AAAA-MM-DD): el título decía «últimos 400 días», que nadie cuenta así. */
  const [desdeStats, setDesdeStats] = useState("");
  /** Las etiquetas de los datos del cliente («Primer nombre»), para no mostrar la clave cruda («primerNombre»). */
  const [campos, setCampos] = useState<CampoDisponible[]>([]);

  // El aviso que trajo la navegación se muestra una sola vez: un F5 no lo repite.
  useEffect(() => {
    if ((location.state as { aviso?: string } | null)?.aviso) navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pedir = useCallback(async (): Promise<string> => {
    try {
      const l = leerRespuesta<PlantillaDetalle>(await oficialApi.plantilla(id));
      if (!l.ok) return textoError(l);
      if (!l.data) return "El servidor no devolvió la plantilla.";
      setP(l.data);
      setNoExiste(false);
      return "";
    } catch (e) {
      const x = leerError(e);
      if (x.status === 404) { setNoExiste(true); return ""; }
      return textoError(x);
    }
  }, [id]);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(await pedir());
    setCargando(false);
  }, [pedir]);

  useEffect(() => { void cargar(); }, [cargar]);

  // Una vez: las de Reactivación y las de la base (con los campos propios de la marca). Si fallan, se
  // muestra la clave como antes: es un rótulo, no bloquea nada.
  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const [a, b] = await Promise.all([oficialApi.campos("activa"), oficialApi.campos("base")]);
        const lista = [a, b].flatMap((r) => { const l = leerRespuesta<CampoDisponible[]>(r); return l.ok ? l.data ?? [] : []; });
        if (vivo) setCampos(lista);
      } catch { /* queda la clave cruda */ }
    })();
    return () => { vivo = false; };
  }, []);

  // Estadísticas de la plantilla: una vez por id. Si fallan, quedan las métricas de siempre.
  useEffect(() => {
    let vivo = true;
    setStats(null);
    (async () => {
      try {
        const hasta = diaEnZona(new Date());
        const desde = sumarDias(hasta, -(MAX_DIAS_RANGO - 1));
        const l = leerRespuesta<EstadisticasPlantilla>(await estadisticasApi.plantilla(id, { desde, hasta }));
        if (vivo && l.ok && l.data?.fila) { setStats(l.data); setDesdeStats(desde); }
      } catch {
        // sin estadísticas: se muestran las métricas del detalle
      }
    })();
    return () => { vivo = false; };
  }, [id]);

  const polling = usePolling(async () => {
    const e = await pedir();
    if (e) throw new Error(e);
  }, p ? intervaloPlantillas([p.estado]) : null, [id]);

  /** Corre una acción con un solo «ocupado» a la vez: dos clics seguidos no duplican ni borran dos veces. */
  const correr = async (nombre: string, fn: () => Promise<void>) => {
    if (ocupado) return;
    setOcupado(nombre);
    setMensaje(null);
    try {
      await fn();
    } catch (e) {
      setMensaje({ tono: "error", texto: textoErrorAccion(e) });
    } finally {
      setOcupado("");
    }
  };

  const duplicar = () => correr("duplicar", async () => {
    const l = leerRespuesta<PlantillaDetalle>(await oficialApi.duplicarPlantilla(id));
    if (!l.ok || !l.data?.id) { setMensaje({ tono: "error", texto: l.ok ? "El servidor no devolvió la copia." : textoError(l) }); return; }
    navigate(`${R}/plantillas/${enc(l.data.id)}/editar`);
  });

  const sincronizar = () => correr("sincronizar", async () => {
    const l = leerRespuesta<PlantillaDetalle>(await oficialApi.sincronizarPlantilla(id));
    if (!l.ok || !l.data) { setMensaje({ tono: "error", texto: l.ok ? "El servidor no devolvió la plantilla." : textoError(l) }); return; }
    const antes = p?.estado;
    setP(l.data);
    setMensaje({ tono: "ok", texto: antes && antes !== l.data.estado ? `Cambió a «${ESTADO_PLANTILLA[l.data.estado]?.label ?? l.data.estado}».` : "Consultamos a Meta: sigue igual." });
  });

  const reintentarEnvio = () => correr("reintentar", async () => {
    try {
      const l = leerRespuesta<PlantillaDetalle>(await oficialApi.enviarAAprobacion(id));
      if (!l.ok) {
        if (l.code === "CUENTA_NO_VERIFICADA") setMensaje({ tono: "error", texto: "Primero conectá tu número de WhatsApp." });
        else if (l.code === "MARCA_FRENADA") setMensaje({ tono: "error", texto: "La marca está frenada: no se pueden mandar plantillas." });
        else setMensaje({ tono: "error", texto: textoError(l) });
        return;
      }
      if (l.data) setP(l.data);
      setMensaje({ tono: "ok", texto: "Mandada a Meta. Te avisamos acá cuando cambie el estado." });
    } catch (e) {
      const x = leerError(e);
      if (x.code === "NOMBRE_EN_USO") {
        setMensaje({ tono: "error", texto: "WhatsApp ya tiene plantillas con ese nombre. Duplicala: la copia sale con otra versión y otro nombre técnico.", duplicar: true });
        return;
      }
      if (x.code === "TWILIO_ERROR" || x.status === 502) {
        setMensaje({ tono: "error", texto: `No pudimos mandar la plantilla a revisión: ${x.error || "sin detalle"}. Meta todavía no la recibió: podés reintentar.` });
        return;
      }
      throw e;
    } finally {
      await pedir();
    }
  });

  const archivar = () => {
    if (!window.confirm("Se esconde del selector de campañas. No cambia nada en WhatsApp.")) return;
    void correr("archivar", async () => {
      try {
        const l = leerRespuesta<PlantillaDetalle>(await oficialApi.archivarPlantilla(id));
        if (!l.ok || !l.data) { setMensaje({ tono: "error", texto: l.ok ? "El servidor no devolvió la plantilla." : textoError(l) }); return; }
        setP(l.data);
        setMensaje({ tono: "ok", texto: "Archivada. Ya no aparece al armar una campaña." });
      } catch (e) {
        if (leerError(e).code === "PLANTILLA_EN_USO") {
          setMensaje({ tono: "error", texto: "La usa una campaña programada o en curso: esperá a que termine." });
          return;
        }
        throw e;
      }
    });
  };

  const borrar = () => correr("borrar", async () => {
    try {
      const l = leerRespuesta<{ borrada: true; aviso?: string }>(await oficialApi.borrarPlantilla(id));
      if (!l.ok) { setMensaje({ tono: "error", texto: textoError(l) }); setConfirmarBorrar(false); return; }
      setConfirmarBorrar(false);
      navigate(`${R}/plantillas`, { state: { aviso: l.data?.aviso ? `Plantilla borrada. ${l.data.aviso}` : "Plantilla borrada." } });
    } catch (e) {
      setConfirmarBorrar(false);
      if (leerError(e).code === "PLANTILLA_EN_USO") {
        setMensaje({ tono: "error", texto: "La usa una campaña: archivala en vez de borrarla." });
        return;
      }
      throw e;
    }
  });

  const reemplazar = () => correr("reemplazar", async () => {
    const l = leerRespuesta<{ reemplazadas: { id: string; nombre: string; volvioABorrador: boolean }[] }>(await oficialApi.reemplazarEnCampanas(id));
    if (!l.ok) { setMensaje({ tono: "error", texto: textoError(l) }); return; }
    setReemplazadas(l.data?.reemplazadas ?? []);
  });

  // ── Estados de la pantalla ──────────────────────────────────────────────────────────────
  if (cargando && !p && !noExiste) {
    return (
      <div className="space-y-4" aria-busy="true">
        <div className="h-24 rounded-2xl bg-slate-100 animate-pulse" />
        <div className="grid md:grid-cols-2 gap-4"><div className="h-72 rounded-2xl bg-slate-100 animate-pulse" /><div className="h-72 rounded-2xl bg-slate-100 animate-pulse" /></div>
      </div>
    );
  }
  if (noExiste) {
    return (
      <Tarjeta>
        <p className="text-sm font-semibold text-slate-800">No encontramos esta plantilla.</p>
        <Link to={`${R}/plantillas`} className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-violet-700 hover:underline"><ArrowLeft size={14} /> Volver a las plantillas</Link>
      </Tarjeta>
    );
  }
  if (!p) {
    return (
      <Tarjeta>
        <p className="text-sm font-semibold text-red-700">No pudimos traer la plantilla.</p>
        <p className="text-sm text-slate-600 mt-1">{error}</p>
        <button type="button" onClick={() => void cargar()} className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-violet-700 hover:underline"><RefreshCw size={14} /> Reintentar</button>
      </Tarjeta>
    );
  }

  const enRevision = p.estado === "received" || p.estado === "pending";
  const demorada = enRevision && typeof p.horasEnRevision === "number" && p.horasEnRevision > 48;
  const b = desdeDetalle(p);
  const tarjeta = p.tipo === "whatsapp/card";
  const sinCampanas = !p.campanasQueLaUsan?.length;
  const variables = [...(p.variables || [])].sort((x, y) => x.numero - y.numero);
  // La MISMA regla que la lista y el asistente (A1-4, A1-6): una simulada con la cuenta real, o una de
  // otra cuenta de Twilio, figura «aprobada» pero no sirve para una campaña. Sin esto, «Usar en
  // campaña» llevaba al asistente a un paso bloqueado.
  const noUsable = motivoPlantillaNoUsable(p, modo);
  // Con la cuenta real, el chip de estado ya dice «Aprobada en simulado»: el de «Simulada» sobraría.
  const simuladaEnReal = p.estado === "approved" && motivoPlantillaInutilizable(p, modo) === "PLANTILLA_SIMULADA";

  return (
    <div className="space-y-5">
      <Link to={`${R}/plantillas`} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800"><ArrowLeft size={14} /> Plantillas</Link>

      {/* Encabezado */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-xl font-semibold text-slate-900 tracking-tight">{p.nombreVisible}</h2>
            <ChipEstadoPlantilla estado={p.estado} simulado={p.simulado} modo={modo} />
            <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${p.categoria === "MARKETING" ? "bg-violet-50 text-violet-700" : "bg-slate-100 text-slate-600"}`}>
              {CATEGORIA[p.categoria] ?? p.categoria}
            </span>
            {p.archivada && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">Archivada</span>}
            {p.simulado === true && !simuladaEnReal && (
              <span title={CHIP_PLANTILLA.simulada.ayuda} className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${CHIP_PLANTILLA.simulada.cls}`}>
                {CHIP_PLANTILLA.simulada.label}
              </span>
            )}
            {p.deOtraCuenta === true && (
              <span title={CHIP_PLANTILLA.deOtraCuenta.ayuda} className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${CHIP_PLANTILLA.deOtraCuenta.cls}`}>
                {CHIP_PLANTILLA.deOtraCuenta.label}
              </span>
            )}
          </div>
          <p className="text-xs text-slate-400 mt-1"><code className="font-mono text-slate-500">{p.nombreTecnico}</code> · versión {p.version} · {TIPO_PLANTILLA[p.tipo]?.titulo ?? p.tipo}</p>
        </div>
        {/* Editar, mandar a aprobación, duplicar, archivar, borrar y usar en una campaña: del equipo de Deenex. */}
        <SoloEquipo>
          <Acciones p={p} noUsable={noUsable} ocupado={ocupado} onDuplicar={duplicar} onSincronizar={sincronizar} onReintentar={reintentarEnvio}
            onArchivar={archivar} onBorrar={() => setConfirmarBorrar(true)} onReemplazar={reemplazar} />
        </SoloEquipo>
      </div>

      {mensaje && (
        <div role={mensaje.tono === "error" ? "alert" : "status"} className={`flex items-start gap-2 rounded-xl border px-4 py-3 text-sm ${
          mensaje.tono === "ok" ? "border-emerald-200 bg-emerald-50 text-emerald-800" : mensaje.tono === "error" ? "border-red-200 bg-red-50 text-red-700" : "border-amber-200 bg-amber-50 text-amber-800"
        }`}>
          {mensaje.tono === "ok" ? <CheckCircle2 size={16} className="flex-shrink-0 mt-0.5" /> : <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />}
          <div className="flex-1 space-y-2">
            <p>{mensaje.texto}</p>
            {mensaje.duplicar && equipo && (
              <button type="button" onClick={duplicar} disabled={!!ocupado}
                className="inline-flex items-center gap-1.5 rounded-lg bg-white border border-red-200 px-3 py-1.5 text-sm font-semibold text-red-800 hover:bg-red-100 disabled:opacity-50">
                <Copy size={14} /> Duplicar y editar
              </button>
            )}
          </div>
          <button type="button" onClick={() => setMensaje(null)} aria-label="Cerrar el aviso"><X size={14} /></button>
        </div>
      )}

      {polling.detenido && (
        <div className="flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800">
          <AlertTriangle size={15} /> Dejamos de actualizar porque el servidor no responde.
          <button type="button" onClick={polling.reintentar} className="ml-auto font-semibold hover:underline">Reintentar</button>
        </div>
      )}

      {/* Recuadro de estado: una aprobada que no sirve con la cuenta conectada dice por qué y cómo salir, nunca «Lista para usar» */}
      <Tarjeta>
        <p className={`text-sm ${noUsable ? "text-amber-800" : "text-slate-700"}`} data-testid="estado-plantilla-ayuda">{ayudaEstadoPlantilla(p, modo)}</p>
        {enRevision && (
          <div className="mt-3 space-y-2">
            <p className="text-sm text-slate-600">Mandada {horasTexto(p.horasEnRevision)}.</p>
            {demorada && (
              <p className="flex items-start gap-2 text-sm text-amber-800 bg-amber-50 rounded-lg px-3 py-2">
                <AlertTriangle size={15} className="flex-shrink-0 mt-0.5" />
                {/* El ticket lo abre el equipo de Deenex con el proveedor y el Content SID es lo que van a pedir: es del
                    equipo. A una persona de un cliente no se le muestra un identificador que no le sirve para nada. */}
                {equipo
                  ? <span>Pasaron más de 48 h: abrí un ticket con el proveedor con el Content SID <code className="font-mono">{p.contentSid || "(sin Content SID)"}</code>.</span>
                  : <span>Pasaron más de 48 h: avisale a tu contacto de Deenex para que lo reclame.</span>}
              </p>
            )}
          </div>
        )}
        {p.estado === "rejected" && (
          <div className="mt-3 space-y-2">
            {p.rejectionReason && <pre className="text-xs font-mono bg-slate-50 border border-slate-100 rounded-lg px-3 py-2 whitespace-pre-wrap break-words text-slate-700">{p.rejectionReason}</pre>}
            <p className="text-sm font-medium text-slate-800">{accionRechazo(p.rejectionReason)}</p>
          </div>
        )}
        {p.recategorizada && (
          <p className="mt-3 flex items-start gap-2 text-sm text-amber-800 bg-amber-50 rounded-lg px-3 py-2">
            <AlertTriangle size={15} className="flex-shrink-0 mt-0.5" />
            <span>
              Meta la pasó de {CATEGORIA[p.recategorizada.de as keyof typeof CATEGORIA] ?? p.recategorizada.de} a{" "}
              {CATEGORIA[p.recategorizada.a as keyof typeof CATEGORIA] ?? p.recategorizada.a} el {fecha(p.recategorizada.at)}. Ahora cuesta{" "}
              {p.costoPorMensajeUSD === undefined
                ? `${usdPorMensaje(p.costoPorMensajeClienteUSD)} por mensaje`
                : `${usdPorMensaje(p.costoPorMensajeUSD)} por mensaje (costo real; a la marca, ${usdPorMensaje(p.costoPorMensajeClienteUSD)})`}. Las campañas programadas se recalcularon.
            </span>
          </p>
        )}
      </Tarjeta>

      {reemplazadas && (
        <Tarjeta titulo="Campañas actualizadas a esta versión">
          {reemplazadas.length === 0 ? (
            <p className="text-sm text-slate-500">No había campañas en borrador o programadas con la versión anterior.</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {reemplazadas.map((c) => (
                <li key={c.id} className="py-2 flex items-center gap-2 text-sm">
                  <Link to={`${R}/campanas/${enc(c.id)}`} className="font-medium text-violet-700 hover:underline">{c.nombre}</Link>
                  {c.volvioABorrador && <span className="text-xs text-amber-700">volvió a borrador: hay que confirmarla de nuevo</span>}
                </li>
              ))}
            </ul>
          )}
        </Tarjeta>
      )}

      {/* Previa y variables. De a dos recién desde `lg`: en `md` la tarjeta de variables tenía ~230 px para cuatro
          columnas y la tabla se salía de la caja. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-start">
        <PreviaWhatsApp
          texto={previaLocal(b)}
          mediaUrl={b.mediaUrl ? conEjemplos(b.mediaUrl, b) : undefined}
          botones={b.botones}
          pie={tarjeta ? b.pie : undefined}
          encabezadoTexto={tarjeta ? b.encabezadoTexto : undefined}
        />
        <Tarjeta titulo="Variables" sub="La previa usa los ejemplos que se mandaron a Meta.">
          {variables.length === 0 ? (
            <p className="text-sm text-slate-400">Esta plantilla no tiene variables.</p>
          ) : (
            // Las celdas no tenían padding a los costados: «VARIABLENOMBRE» y «Descuento20 %» quedaban pegados. Va
            // con el mismo `pr-4` que las demás tablas de la sección. En el celular cuatro columnas no entran: cada
            // variable es un renglón apilado (la variable y su nombre arriba, el ejemplo y el dato abajo, con su rótulo).
            <div className="sm:overflow-x-auto">
              <table className="block w-full text-sm sm:table">
                <thead className="hidden sm:table-header-group">
                  <tr className="text-left">
                    {["Variable", "Nombre", "Ejemplo", "Dato sugerido"].map((h, i, todas) => (
                      <th key={h} scope="col" className={`pb-2 text-[10px] font-bold uppercase tracking-wider text-slate-400 ${i < todas.length - 1 ? "pr-4" : ""}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="block divide-y divide-slate-50 sm:table-row-group">
                  {variables.map((v) => (
                    <tr key={v.numero} className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 py-2 align-top sm:table-row sm:py-0">
                      <td className="sm:py-2 pr-4 font-mono text-xs text-violet-700 whitespace-nowrap">{`{{${v.numero}}}`}</td>
                      <td className="sm:py-2 pr-4 text-slate-700 break-words">{v.alias}</td>
                      <td className="col-start-2 sm:py-2 pr-4 text-slate-700 break-words before:content-['Ejemplo:_'] before:text-xs before:text-slate-400 sm:before:content-none">
                        {v.ejemplo || <span className="text-slate-400">—</span>}
                      </td>
                      <td className="col-start-2 sm:py-2 text-slate-500 break-words before:content-['Dato_sugerido:_'] before:text-xs before:text-slate-400 sm:before:content-none">
                        {etiquetaDelCampo(campos, v.campoSugerido)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Tarjeta>
      </div>

      {/* Métricas */}
      <section>
        <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Métricas{stats && desdeStats ? <span className="ml-1 normal-case font-normal tracking-normal text-slate-400">· desde el {fecha(`${desdeStats}T12:00:00`)}</span> : null}</h3>
        {stats ? (
          <MetricasEstadisticas s={stats} p={p} />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <StatTile icon={<Mail size={15} />} label="Entregados" tono="entrega" value={sinCampanas ? null : num(p.metricas?.entregados ?? 0)} vacio="Todavía no se usó" />
            <StatTile icon={<Eye size={15} />} label="Leídos" tono="lectura" value={sinCampanas ? null : num(p.metricas?.leidos ?? 0)} vacio="Todavía no se usó" />
            <StatTile icon={<UserX size={15} />} label="Bajas" tono="baja" value={sinCampanas ? null : num(p.metricas?.bajas ?? 0)} vacio="Todavía no se usó" />
          </div>
        )}
      </section>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 items-start">
        <Tarjeta titulo="Campañas que la usan">
          {sinCampanas ? (
            <p className="text-sm text-slate-400">Ninguna.</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {p.campanasQueLaUsan.map((c) => (
                <li key={c.id} className="py-2 flex items-center gap-2">
                  <Link to={`${R}/campanas/${enc(c.id)}`} className="text-sm font-medium text-slate-800 hover:text-violet-700 truncate">{c.nombre}</Link>
                  <ChipEstadoCampanaOficial estado={c.estado} className="ml-auto" />
                </li>
              ))}
            </ul>
          )}
        </Tarjeta>

        <Tarjeta titulo="Historial">
          {!p.historial?.length ? (
            <p className="text-sm text-slate-400">Sin movimientos todavía.</p>
          ) : (
            <ol className="relative border-l border-slate-200 ml-2 space-y-3">
              {[...p.historial].sort((x, y) => new Date(y.at).getTime() - new Date(x.at).getTime()).map((h, i) => (
                <li key={`${h.at}-${i}`} className="ml-4">
                  <span className="absolute -left-1.5 mt-1.5 w-3 h-3 rounded-full bg-white border-2 border-slate-300" />
                  <div className="flex items-center gap-2 flex-wrap">
                    <ChipEstadoPlantilla estado={h.estado} />
                    <span className="text-xs text-slate-500">{fechaHora(h.at)} · {FUENTE_HISTORIAL[h.fuente] ?? h.fuente}</span>
                  </div>
                  {h.detalle && <p className="text-xs text-slate-500 mt-0.5">{h.detalle}</p>}
                </li>
              ))}
            </ol>
          )}
        </Tarjeta>
      </div>

      <section className="rounded-2xl border border-slate-100 bg-white shadow-sm">
        <button type="button" onClick={() => setVerTecnicos((v) => !v)} aria-expanded={verTecnicos}
          className="w-full flex items-center gap-2 px-6 py-4 text-sm font-semibold text-slate-700">
          {verTecnicos ? <ChevronDown size={15} /> : <ChevronRight size={15} />} Datos técnicos
        </button>
        {verTecnicos && (
          <dl className="px-6 pb-5 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
            {/* El Content SID es el identificador de la plantilla en Twilio: del equipo (un cliente no sabe qué es). */}
            {equipo && <><dt className="text-slate-500">Content SID</dt><dd className="font-mono text-slate-800 break-all">{p.contentSid || "—"}</dd></>}
            <dt className="text-slate-500">Idioma</dt><dd className="text-slate-800">{p.idioma}</dd>
            <dt className="text-slate-500">Categoría pedida</dt><dd className="text-slate-800">{CATEGORIA[p.categoriaSolicitada] ?? p.categoriaSolicitada}</dd>
            {/* Con `nombreDeQuien` (la regla de `autorDe` del backend): decía el email del token tal cual, «alan vía hatsu-demo@operador.deenex».
                A una persona de un cliente, alguien del equipo es «Equipo Deenex», sin su mail. */}
            <dt className="text-slate-500">Creada por</dt><dd className="text-slate-800">{nombreDeQuienPara(p.creadoPor?.email, equipo) || "—"}</dd>
            <dt className="text-slate-500">Mandada por</dt><dd className="text-slate-800">{nombreDeQuienPara(p.enviadaPor?.email, equipo) || "—"}</dd>
            <dt className="text-slate-500">Mandada a Meta</dt><dd className="text-slate-800">{fechaHora(p.enviadaAAprobacionAt || undefined)}</dd>
            <dt className="text-slate-500">Aprobada</dt><dd className="text-slate-800">{fechaHora(p.aprobadaAt || undefined)}</dd>
          </dl>
        )}
      </section>

      {confirmarBorrar && (
        <ConfirmDangerModal
          title="Borrar plantilla"
          description={p.estado === "borrador" && !p.contentSid
            ? "Se borra el borrador."
            : `Se borra también en WhatsApp. El nombre ${p.nombreTecnico} no se puede reusar por 30 días.`}
          onConfirm={() => void borrar()}
          onCancel={() => setConfirmarBorrar(false)}
          busy={ocupado === "borrar"}
        />
      )}
    </div>
  );
}

/** Seis números, la aprobación, la recategorización y los botones tocados (§B.7.3). */
function MetricasEstadisticas({ s, p }: { s: EstadisticasPlantilla; p: PlantillaDetalle }) {
  const f = s.fila;
  const sinEnvios = !(Number(f.enviados) > 0);
  const tasa = (t: number | undefined) => (sinEnvios ? null : tasaTexto(t));
  const botones = [...(s.botones || [])].sort((a, b) => (Number(b.cantidad) || 0) - (Number(a.cantidad) || 0)).slice(0, 10);
  const horas = f.horasHastaAprobacion;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <StatTile icon={<Send size={15} />} label="Enviados" tono="entrega" value={sinEnvios ? null : num(f.enviados)} vacio="Todavía no se usó"
          sub={`${num(f.campanas)} ${f.campanas === 1 ? "campaña" : "campañas"}`} />
        {/* cada tasa dice su base (entrega y bajas sobre los enviados; lectura y respuesta sobre los entregados) */}
        <StatTile icon={<CheckCheck size={15} />} label="Entrega" tono="entrega" value={tasa(f.tasas?.entrega)} vacio="Sin envíos"
          meterPct={sinEnvios ? undefined : (f.tasas?.entrega || 0) * 100} sub={sobreLaBase(f.entregados, f.enviados, BASE_TASA.entrega)} />
        <StatTile icon={<Eye size={15} />} label="Lectura" tono="lectura" value={tasa(f.tasas?.lectura)} vacio="Sin envíos"
          sub={`${sobreLaBase(f.leidos, f.entregados, BASE_TASA.lectura)} · sólo con confirmación de lectura`} />
        <StatTile icon={<MessageSquare size={15} />} label="Respuesta" tono="respuesta" value={tasa(f.tasas?.respuesta)} vacio="Sin envíos" sub={sobreLaBase(f.respondieron, f.entregados, BASE_TASA.respuesta)} />
        <StatTile icon={<UserX size={15} />} label="Bajas" tono="baja" value={tasa(f.tasas?.bajas)} vacio="Sin envíos" sub={sobreLaBase(f.bajas, f.enviados, BASE_TASA.bajas)} />
        <StatTile icon={<Wallet size={15} />} label="Gasto" tono="resultado" value={usd(f.cobradoClienteUSD)} sub="Cobrado a la marca" />
      </div>
      <div className="text-sm text-slate-600 space-y-1">
        {horas !== null && Number.isFinite(Number(horas)) && <p>Aprobada en {num(Math.round(Number(horas) * 10) / 10)} h.</p>}
        {p.recategorizada && (
          <p>Meta la recategorizó de {CATEGORIA[p.recategorizada.de as keyof typeof CATEGORIA] ?? p.recategorizada.de} a {CATEGORIA[p.recategorizada.a as keyof typeof CATEGORIA] ?? p.recategorizada.a}.</p>
        )}
        {f.rechazos > 0 && <p>Meta la rechazó {num(f.rechazos)} {f.rechazos === 1 ? "vez" : "veces"}{f.ultimoMotivoRechazo ? ` (último motivo: ${f.ultimoMotivoRechazo})` : ""}.</p>}
      </div>
      <Tarjeta titulo="Botones tocados">
        {!botones.length ? (
          <p className="text-sm text-slate-400">Nadie tocó un botón de esta plantilla todavía.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {botones.map((b) => (
              <li key={`${b.payload}|${b.etiqueta}`} className="py-2 flex items-center gap-2 text-sm">
                <span className="text-slate-800">{b.etiqueta || b.payload || "(sin texto)"}</span>
                {b.esBaja && <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-red-50 text-red-600">Baja</span>}
                <span className="ml-auto font-semibold text-slate-900 tabular-nums">{num(b.cantidad)}</span>
              </li>
            ))}
          </ul>
        )}
      </Tarjeta>
    </div>
  );
}

function textoErrorAccion(e: unknown): string {
  const x = leerError(e);
  if (esErrorDeClave(x.code)) return TEXTO_ERROR_CLAVE;
  if (x.code === "PLANTILLA_NO_EDITABLE") return "Esta plantilla ya se mandó. Recargá la página.";
  return textoError(x);
}

function Acciones({ p, noUsable, ocupado, onDuplicar, onSincronizar, onReintentar, onArchivar, onBorrar, onReemplazar }: {
  p: PlantillaDetalle; noUsable: string | null; ocupado: string; onDuplicar: () => void; onSincronizar: () => void; onReintentar: () => void;
  onArchivar: () => void; onBorrar: () => void; onReemplazar: () => void;
}) {
  const primario = "inline-flex items-center gap-1.5 rounded-xl bg-violet-600 px-3.5 py-2 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-50";
  const secundario = "inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50";
  const peligro = "inline-flex items-center gap-1.5 rounded-xl border border-red-200 bg-white px-3.5 py-2 text-sm font-semibold text-red-600 hover:bg-red-50 disabled:opacity-50";
  const cargando = (n: string, icono: JSX.Element) => (ocupado === n ? <Loader2 size={15} className="animate-spin" /> : icono);
  const d = !!ocupado;
  const id = encodeURIComponent(p.id);

  const botones: JSX.Element[] = [];
  let aviso: string | null = null;
  switch (p.estado) {
    case "borrador":
      botones.push(
        <Link key="editar" to={`${R}/plantillas/${id}/editar`} className={primario}><Pencil size={15} /> Editar</Link>,
        <button key="borrar" type="button" onClick={onBorrar} disabled={d} className={peligro}>{cargando("borrar", <Trash2 size={15} />)} Borrar</button>,
      );
      break;
    case "unsubmitted":
      botones.push(
        <button key="reintentar" type="button" onClick={onReintentar} disabled={d} className={primario}>{cargando("reintentar", <Send size={15} />)} Reintentar envío a aprobación</button>,
        <button key="duplicar" type="button" onClick={onDuplicar} disabled={d} className={secundario}>{cargando("duplicar", <Copy size={15} />)} Duplicar y editar</button>,
        <button key="borrar" type="button" onClick={onBorrar} disabled={d} className={peligro}>{cargando("borrar", <Trash2 size={15} />)} Borrar</button>,
      );
      break;
    case "received":
    case "pending":
      // Mandada no se edita: si tiene un error, se duplica y se corrige la copia, sin esperar la respuesta de Meta
      // (Llama Leads, 26/10/2026: «no te deja editar la plantilla porque ya la mandaste»).
      botones.push(
        <button key="sinc" type="button" onClick={onSincronizar} disabled={d} className={secundario}>{cargando("sincronizar", <RefreshCw size={15} />)} Consultar a Meta</button>,
        <button key="duplicar" type="button" onClick={onDuplicar} disabled={d} className={secundario}>{cargando("duplicar", <Copy size={15} />)} Duplicar y editar</button>,
      );
      break;
    case "approved":
      if (noUsable) {
        // No sirve con la cuenta conectada: en vez de «Usar en campaña» (que el asistente y el backend
        // frenan), se dice por qué y la salida es duplicarla y mandarla a aprobación.
        aviso = noUsable;
        botones.push(<button key="duplicar" type="button" onClick={onDuplicar} disabled={d} className={primario}>{cargando("duplicar", <Copy size={15} />)} Duplicar y editar</button>);
      } else {
        botones.push(
          <Link key="usar" to={`${R}/campanas/nueva?plantilla=${id}`} className={primario}><MousePointerClick size={15} /> Usar en campaña</Link>,
          <button key="duplicar" type="button" onClick={onDuplicar} disabled={d} className={secundario}>{cargando("duplicar", <Copy size={15} />)} Duplicar y editar</button>,
        );
      }
      if (!p.archivada) botones.push(<button key="archivar" type="button" onClick={onArchivar} disabled={d} className={secundario}>{cargando("archivar", <Archive size={15} />)} Archivar</button>);
      // Pasarla a las campañas de la versión anterior las dejaría con una plantilla que no se puede
      // mandar: el backend lo rechaza con el mismo código, así que tampoco se ofrece.
      if (p.versionAnteriorId && !noUsable) {
        botones.push(
          <button key="reemplazar" type="button" onClick={onReemplazar} disabled={d} className={secundario}>
            {cargando("reemplazar", <Replace size={15} />)} Usar esta versión en las campañas de la anterior
          </button>,
        );
      }
      break;
    case "rejected":
      botones.push(
        <button key="duplicar" type="button" onClick={onDuplicar} disabled={d} className={primario}>{cargando("duplicar", <Copy size={15} />)} Duplicar y editar</button>,
        <button key="borrar" type="button" onClick={onBorrar} disabled={d} className={peligro}>{cargando("borrar", <Trash2 size={15} />)} Borrar</button>,
      );
      break;
    case "paused":
    case "disabled":
      botones.push(<button key="duplicar" type="button" onClick={onDuplicar} disabled={d} className={secundario}>{cargando("duplicar", <Copy size={15} />)} Duplicar y editar</button>);
      if (!p.archivada) botones.push(<button key="archivar" type="button" onClick={onArchivar} disabled={d} className={secundario}>{cargando("archivar", <Archive size={15} />)} Archivar</button>);
      break;
    case "eliminada":
      break;
  }
  if (!botones.length) return null;
  return (
    <div className="space-y-2">
      {aviso && (
        <p className="flex items-start gap-1.5 max-w-md text-xs text-amber-800" data-testid="plantilla-no-usable">
          <AlertTriangle size={13} className="mt-0.5 flex-shrink-0" /> {aviso}
        </p>
      )}
      <div className="flex flex-wrap gap-2">{botones}</div>
    </div>
  );
}
